/* Studio · Overview — daily business control centre (Phase 2A).
   Deterministic, read-only, LIVE data by default. No AI calls on load. */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Camera, Inbox, Banknote } from "lucide-react";
import { studioHeaders } from "@/lib/studioAuth";
import NewInquiryModal from "@/pages/studio/crm/NewInquiryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIO = {
    critical: { label: "Critical", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-600" },
    today: { label: "Today", cls: "bg-[#B8814F]/10 text-[#96683D] border-[#B8814F]/30", dot: "bg-[#B8814F]" },
    upcoming: { label: "Upcoming", cls: "bg-cafe-latte text-cafe-mocha border-border", dot: "bg-cafe-mocha/50" },
};
const CATS = [["all", "View all"], ["enquiries", "New enquiries"], ["followups", "Follow-ups"], ["quotations", "Quotations"], ["payments", "Payments"], ["shoots", "Upcoming shoots"], ["review", "Client review"], ["balances", "Balances due"], ["deliver", "Ready to deliver"], ["products", "Digital Products"]];

function Card({ label, value, tip, onClick, accent, sub }) {
    return (
        <button onClick={onClick} title={tip}
            className="text-left rounded-2xl bg-white border border-border p-4 hover:border-cafe-olive transition-colors focus:outline-none focus:ring-2 focus:ring-cafe-olive/40"
            data-testid={`snap-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="font-serif text-2xl sm:text-3xl" style={{ color: accent || "#3D2F23" }}>{value}</p>
            <p className="text-[10.5px] uppercase tracking-[0.12em] text-cafe-mocha mt-1">{label}</p>
            {sub && <p className="text-[10.5px] text-cafe-olive mt-0.5">{sub}</p>}
        </button>
    );
}

function SectionTitle({ children, action }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-cafe-espresso">{children}</h2>
            {action}
        </div>
    );
}

export default function OverviewTab({ setNav }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const [demo, setDemo] = useState(false);
    const [showInquiry, setShowInquiry] = useState(false);
    const [cat, setCat] = useState("all");
    const [busyId, setBusyId] = useState(null);

    const refresh = async () => {
        try {
            const r = await axios.get(`${API}/studio/overview`, { params: { include_demo: demo }, headers: studioHeaders() });
            setD(r.data);
        } catch (e) { setErr(e?.response?.data?.detail || "Failed to load overview"); }
    };

    useEffect(() => {
        setD(null);
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demo]);

    // 2026-10-08 — one-click quick actions on attention cards. Kaily can mark a
    // lead Lost / Won, snooze follow-up 3 or 7 days, or WhatsApp them without
    // ever opening the modal. Lead-specific actions use the lead id extracted
    // from the href. Non-lead items (shoot orders, product orders) still just
    // link through — they live in different collections.
    const leadIdFromHref = (href) => {
        const m = /[?&]lead=([^&]+)/.exec(href || "");
        return m ? m[1] : null;
    };
    const quickCall = async (href, path, body = {}) => {
        const lid = leadIdFromHref(href);
        if (!lid) return;
        setBusyId(lid);
        try {
            await axios.post(`${API}/crm/leads/${lid}${path}`, body, { headers: studioHeaders() });
            await refresh();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Quick action failed");
        } finally {
            setBusyId(null);
        }
    };

    if (err) return <p className="text-red-700 text-sm" data-testid="overview-error">{err}</p>;
    if (!d) return <div className="py-16 text-center text-cafe-mocha"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

    const now = new Date();
    const h = Number(new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: "Asia/Kuala_Lumpur" }).format(now));
    const greet = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
    const dateStr = new Intl.DateTimeFormat("en-MY", { weekday: "long", day: "numeric", month: "long", timeZone: "Asia/Kuala_Lumpur" }).format(now);
    const items = (d.attention || []).filter((a) => cat === "all" || a.category === cat);
    const s = d.snapshot || {};
    const p = d.pipeline || {};
    const pr = d.products || {};
    const rm = (v) => `RM ${(v || 0).toLocaleString()}`;

    return (
        <div className="space-y-8 max-w-5xl" data-testid="studio-overview">
            <NewInquiryModal open={showInquiry} onClose={() => setShowInquiry(false)} onCreated={() => window.location.assign("/studio?tab=sales&sub=crm")} />
            {/* 1 · HEADER */}
            <section>
                <p className="text-[11px] uppercase tracking-[0.24em] text-cafe-mocha/70">{dateStr} · Kuala Lumpur</p>
                <h1 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mt-1.5">{greet}, Kaily.</h1>
                <p className="text-sm text-cafe-mocha mt-1.5">Here is what needs your attention today.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                    {[
                        [Plus, "New Inquiry", () => setShowInquiry(true)],
                        [Camera, "Client Projects", () => setNav("clients")],
                        [Inbox, "Leads & Sales", () => setNav("sales", "crm")],
                        [Banknote, "Record Payment", () => setNav("sales", "crm")],
                    ].map(([Icon, label, fn]) => (
                        <button key={label} onClick={fn} data-testid={`qa-${label.toLowerCase().replace(/ /g, "-")}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-cafe-espresso/20 bg-white px-4 py-2 text-xs font-semibold text-cafe-espresso hover:border-cafe-olive transition-colors">
                            <Icon className="h-3.5 w-3.5 text-cafe-olive" /> {label}
                        </button>
                    ))}
                    <label className="ml-auto inline-flex items-center gap-2 text-[11px] text-cafe-mocha cursor-pointer select-none" data-testid="demo-toggle">
                        <input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} className="accent-cafe-olive" />
                        Include demo/test {demo ? "" : "(live only)"}
                    </label>
                </div>
            </section>

            {/* 2 · NEEDS YOUR ATTENTION */}
            <section className="rounded-3xl border border-border bg-cafe-latte/40 p-5 sm:p-6" data-testid="attention-section">
                <SectionTitle>Needs your attention {items.length > 0 && <span className="text-cafe-olive">({items.length})</span>}</SectionTitle>
                <details className="mb-3 text-xs text-cafe-mocha" data-testid="attention-guide">
                    <summary className="cursor-pointer select-none text-[11px] text-cafe-olive">? 这里是什么 · How this works</summary>
                    <div className="mt-2 space-y-1 bg-white/70 rounded-xl p-3">
                        <div>这里自动列出<b>今天需要你处理的事</b>，按紧急程度排序：<b>CRITICAL</b>（已逾期）→ <b>TODAY</b>（今天该做）。</div>
                        <div>每行右边的 "→ …" 就是建议的下一步动作，点击该行可直接跳到对应的 Lead / 项目 / 付款去处理。</div>
                        <div>处理完成后（例如记录了交付、发了订金链接），该提醒会自动消失。</div>
                    </div>
                </details>
                <div className="flex flex-wrap gap-1.5 mb-4">
                    {CATS.map(([k, label]) => (
                        <button key={k} onClick={() => setCat(k)} data-testid={`att-filter-${k}`}
                            className={`rounded-full px-3 py-1 text-[11px] font-semibold border transition-colors ${cat === k ? "border-cafe-olive bg-cafe-olive/10 text-cafe-olive" : "border-border text-cafe-mocha hover:text-cafe-espresso"}`}>
                            {label}
                        </button>
                    ))}
                </div>
                {items.length === 0 ? (
                    <p className="text-sm text-cafe-mocha py-4" data-testid="attention-empty">No follow-ups due today. All clear ✓</p>
                ) : (
                    <div className="space-y-2">
                        {items.map((a, i) => {
                            const pc = PRIO[a.priority] || PRIO.upcoming;
                            const lid = leadIdFromHref(a.href);
                            const busy = busyId && busyId === lid;
                            return (
                                <div key={i} data-testid={`attention-item-${i}`}
                                    className="flex items-start sm:items-center gap-3 rounded-2xl bg-white border border-border p-3.5 hover:border-cafe-olive transition-colors flex-wrap sm:flex-nowrap">
                                    <a href={a.href} className="contents">
                                        <span className={`mt-1.5 sm:mt-0 h-2 w-2 rounded-full shrink-0 ${pc.dot}`} />
                                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em] ${pc.cls}`}>{pc.label}</span>
                                        <span className="flex-1 min-w-[180px]">
                                            <span className="block text-sm font-semibold text-cafe-espresso">{a.name}</span>
                                            <span className="block text-[12px] text-cafe-mocha">{a.reason}</span>
                                        </span>
                                        {a.value_rm != null && a.value_rm > 0 && <span className="text-sm font-bold text-cafe-olive shrink-0">{rm(a.value_rm)}</span>}
                                    </a>
                                    {/* Inline quick actions for leads — 2026-10-08. For non-lead
                                        items (shoot orders) we just render the → action link. */}
                                    {lid ? (
                                        <div className="flex items-center gap-1.5 shrink-0 w-full sm:w-auto">
                                            <button
                                                type="button"
                                                disabled={busy}
                                                data-testid={`attn-snooze-3d-${i}`}
                                                onClick={() => quickCall(a.href, "/followup", { preset: "3d" })}
                                                className="h-7 px-2.5 rounded-full border border-border text-[11px] text-cafe-mocha hover:border-cafe-olive hover:text-cafe-espresso disabled:opacity-50"
                                                title="Snooze 3 days">
                                                Snooze 3d
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                data-testid={`attn-lost-${i}`}
                                                onClick={() => quickCall(a.href, "/lost", {})}
                                                className="h-7 px-2.5 rounded-full border border-red-200 bg-red-50 text-[11px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                                                title="Mark Lost — removes from Overview immediately">
                                                Lost
                                            </button>
                                            <a href={a.href} className="h-7 px-2.5 rounded-full bg-cafe-olive text-cafe-cream text-[11px] font-semibold inline-flex items-center hover:bg-cafe-oliveDark">
                                                Open
                                            </a>
                                        </div>
                                    ) : (
                                        <a href={a.href} className="text-[11px] font-semibold text-cafe-olive shrink-0">→ {a.action}</a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* 3 · BUSINESS SNAPSHOT */}
            <section data-testid="snapshot-section">
                <SectionTitle>Business snapshot</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <Card label="Paid revenue this month" value={rm(s.paid_revenue_month)} accent="#5B6B3C"
                        sub={s.revenue_change_pct != null ? `${s.revenue_change_pct >= 0 ? "+" : ""}${s.revenue_change_pct}% vs last month` : null}
                        tip="Payments successfully received this KL-time month: paid product orders + project deposits + final balances. Excludes demo/test, cancelled & refunded."
                        onClick={() => setNav("settings", "orders")} />
                    <Card label="Open pipeline value" value={rm(s.pipeline_value)}
                        tip="Sum of quote values on open leads (new / proposal sent / negotiation). Excludes won, lost and spam."
                        onClick={() => setNav("sales", "crm")} />
                    <Card label="Outstanding balances" value={rm(s.outstanding_balance)} accent={s.outstanding_balance > 0 ? "#B8814F" : undefined}
                        tip="Confirmed unpaid client balances on delivered projects awaiting final payment."
                        onClick={() => setNav("clients")} />
                    <Card label="Active client projects" value={s.active_projects ?? 0}
                        tip="Projects with deposit paid or awaiting final balance. Excludes cancelled and fully paid."
                        onClick={() => setNav("clients")} />
                    <Card label="Subscription MRR" value={rm(s.mrr)}
                        tip="Active recurring subscription revenue only (live subscribers × plan price)."
                        onClick={() => setNav("settings", "subscribers")} />
                </div>
            </section>

            {/* 4 · SALES PIPELINE */}
            <section className="rounded-3xl border border-border bg-white p-5 sm:p-6" data-testid="pipeline-section">
                <SectionTitle action={
                    <button onClick={() => setNav("sales", "crm")} className="text-[11px] uppercase tracking-[0.12em] font-bold text-cafe-olive hover:text-cafe-espresso">Open Sales CRM →</button>
                }>Sales pipeline</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {(d.stages8 || []).map((st) => (
                        <div key={st.key} className="rounded-xl bg-cafe-latte/50 border border-border px-3 py-2.5 text-center">
                            <p className="font-serif text-2xl text-cafe-espresso">{st.count}</p>
                            <p className="text-[10px] uppercase tracking-[0.1em] text-cafe-mocha">{st.label}</p>
                        </div>
                    ))}
                </div>
                <p className="text-[12px] text-cafe-mocha mt-3">
                    Pipeline value {rm(p.value)} · {p.followups_due || 0} follow-ups due
                    {p.won_rate != null && <> · {p.won_rate}% won of closed</>}
                </p>
            </section>

            {/* 5 · CLIENT PROJECTS */}
            <section className="rounded-3xl border border-border bg-white p-5 sm:p-6" data-testid="projects-section">
                <SectionTitle action={
                    <button onClick={() => setNav("clients")} className="text-[11px] uppercase tracking-[0.12em] font-bold text-cafe-olive hover:text-cafe-espresso">Open Client Projects →</button>
                }>Client projects</SectionTitle>
                {(d.projects || []).length === 0 ? (
                    <p className="text-sm text-cafe-mocha" data-testid="projects-empty">No active client projects.</p>
                ) : (
                    <div className="space-y-2">
                        {d.projects.map((pj) => (
                            <div key={pj.id} className="flex items-center gap-3 flex-wrap rounded-xl border border-border px-4 py-3">
                                <span className="text-sm font-semibold text-cafe-espresso flex-1 min-w-[140px]">{pj.name}</span>
                                <span className="text-[12px] text-cafe-mocha">{pj.package}</span>
                                <span className="text-sm font-bold text-cafe-olive">{rm(pj.value_rm)}</span>
                                <span className="rounded-full bg-cafe-latte px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em] font-bold text-cafe-mocha">{(pj.stage || "").replace(/_/g, " ")}</span>
                                <span className="text-[11px] font-semibold text-cafe-olive">→ {pj.next_action}</span>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* 6 · DIGITAL PRODUCTS */}
            <section className="rounded-3xl border border-border bg-white p-5 sm:p-6" data-testid="products-section">
                <SectionTitle action={
                    <button onClick={() => setNav("settings", "orders")} className="text-[11px] uppercase tracking-[0.12em] font-bold text-cafe-olive hover:text-cafe-espresso">Open Product Orders →</button>
                }>Digital products</SectionTitle>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {[["Awaiting brief", pr.awaiting_brief, () => setNav("settings", "orders")],
                      ["Generating", pr.generating, () => setNav("settings", "orders")],
                      ["Ready for review", pr.ready_for_review, () => setNav("settings", "orders"), pr.ready_for_review > 0 ? "#B8814F" : null],
                      ["Ready to deliver", pr.ready_to_deliver, () => setNav("settings", "orders"), pr.ready_to_deliver > 0 ? "#B8814F" : null],
                      ["Active subs", pr.subs_active, () => setNav("settings", "subscribers")],
                      ["Past-due subs", pr.subs_past_due, () => setNav("settings", "subscribers"), pr.subs_past_due > 0 ? "#B91C1C" : null],
                    ].map(([label, v, fn, accent]) => (
                        <button key={label} onClick={fn} className="rounded-xl bg-cafe-latte/50 border border-border px-2 py-2.5 text-center hover:border-cafe-olive transition-colors">
                            <p className="font-serif text-xl" style={{ color: accent || "#3D2F23" }}>{v ?? 0}</p>
                            <p className="text-[9.5px] uppercase tracking-[0.08em] text-cafe-mocha leading-tight mt-0.5">{label}</p>
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap gap-3 mt-3 text-[11px] font-semibold text-cafe-olive">
                    <span className="text-cafe-mocha font-normal">Delivered (historical): {pr.delivered ?? 0}</span>
                    <button onClick={() => setNav("products", "system")} className="hover:text-cafe-espresso">Monthly Editions →</button>
                    <button onClick={() => setNav("products", "digests")} className="hover:text-cafe-espresso">Delivery Emails →</button>
                </div>
            </section>

            {/* 7 · RECENT ACTIVITY */}
            <section data-testid="activity-section">
                <SectionTitle>Recent business activity</SectionTitle>
                {(d.activity || []).length === 0 ? (
                    <p className="text-sm text-cafe-mocha">No recent activity.</p>
                ) : (
                    <div className="space-y-1">
                        {d.activity.map((a, i) => (
                            <a key={i} href={a.href} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-cafe-latte/60 transition-colors">
                                <span className="text-[11px] text-cafe-mocha/70 w-32 shrink-0 tabular-nums">
                                    {new Intl.DateTimeFormat("en-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kuala_Lumpur" }).format(new Date(a.at))}
                                </span>
                                <span className="text-[12.5px] text-cafe-espresso font-medium">{a.name}</span>
                                <span className="text-[12px] text-cafe-mocha">— {a.event}</span>
                            </a>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
